import cn from 'clsx'
import React from 'react'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import BodyText from 'LuxKit/Typography/BodyText'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import { OfferFilterSelectButton, OfferFilterSelectButtonContent } from './OfferFilterSelectButton'

interface Props extends React.HTMLAttributes<HTMLElement> {
  disabled?: boolean
  /**
   * Whether or not the component is currently 'active'
   * In this context, that usually means that the select/dropdown related to it is open
   */
  active?: boolean
  /**
   * The icon to be shown for this control
   */
  icon: React.ReactNode
  /**
   * Whether or not this is being show in the mobile sidebar or not
   * This changes the arrow direction to be sideways instead
   */
  inSidebar?: boolean
  /**
   * Whether or not this select box is in an invalid state
   * Will show the `invalidMessage` prop via the native pop up when validated
   */
  invalid?: boolean
  /**
   * The label that identifies what this select box is for
   */
  label: string
  /**
   * React node allowed so you define which parts of it can be bold via the `<b>` tag
   * Please avoid using other, more complex components
   */
  value?: React.ReactNode
  /**
   * The value to show when there is no value set
   */
  placeholder: string
  /**
   * Message to show when the input is invalid
   * Will appear in the native pop up on validation
   */
  invalidMessage?: string
}

const OfferFilterDropdownBox = React.forwardRef<HTMLButtonElement | HTMLDivElement, Props>((props, ref) => {
  const {
    label,
    value,
    placeholder,
    active,
    inSidebar,
    icon,
    invalid,
    className,
    invalidMessage,
    onClick,
    disabled,
    ...rest
  } = props

  const hasClick = !!onClick && !disabled
  return (
    <OfferFilterSelectButton
      {...rest}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      as={hasClick ? undefined : 'div'}
      ref={ref as any}
      className={cn(
        className,
        {
          active,
          invalid,
        },
      )}
    >
      {invalid && <HiddenInput required invalidMessage={invalidMessage} />}
      {icon}
      <OfferFilterSelectButtonContent>
        <BodyText variant="small" wrap="truncate">{label}</BodyText>
        <BodyText variant="medium" colour={value ? 'neutral-two' : 'neutral-four'} wrap="truncate">
          {value || placeholder}
        </BodyText>
      </OfferFilterSelectButtonContent>
      {hasClick && <DropdownChevron
        direction={inSidebar ? 'right' : undefined}
        open={inSidebar ? undefined : active}
      />}
    </OfferFilterSelectButton>
  )
})

OfferFilterDropdownBox.displayName = 'OfferFilterSelectBox'

export default OfferFilterDropdownBox
